/**
 * Reading repeat structure out of ASCII tab. Spec 2026-08-30.
 *
 * Two shapes, both pure and both column-based, so the parser can hand this module a section's rows
 * and get back marks it can map onto steps. Nothing here knows about steps, and nothing here writes
 * to `source`.
 */

/**
 * The dot that turns a barline into a repeat sign. `o` is the ASCII spelling of the two dots of
 * 𝄆/𝄇, `:` is the typographic one; `O` is the same dot shouted.
 */
const isRepeatDot = (char: string | undefined) => char === 'o' || char === 'O' || char === ':'

/** A barline — one column, or a run of adjacent ones — and the repeat dots standing against it. */
export interface RepeatMark {
  /** First column of the barline run. */
  startColumn: number
  /** Last column of the barline run. Equal to `startColumn` for a single `|`. */
  endColumn: number
  /** A dot stands immediately after the run: `||o`, `|o`, `|:`. */
  begins: boolean
  /** A dot stands immediately before the run: `o||`, `o|`, `:|`. */
  ends: boolean
}

/**
 * The last character of a row before its own closing bar, or undefined for a row that has none.
 *
 * The closing bar is the one bar that does not stand at a single column. Transcribers pad rows by
 * eye, so a dot's row is routinely narrower than the widest row, and the trailing bars step in and
 * out with them. Trailing `|`s that survived the content strip belong to that row's closing bar
 * too, so `o||` and `o|` both put the dot one character further back than the row's last index.
 *
 * Right-trimming the rows to a common width would make one column work, and is not an option:
 * trailing dashes are rests, and trimming them moves the steps.
 */
const beforeOwnClosingBar = (content: string): string | undefined => {
  let index = content.length - 1
  while (index >= 0 && content[index] === '|') {
    index--
  }
  return content[index]
}

/**
 * The repeat marks in one section.
 *
 * `barlineColumns` must already be the columns where a barline stands on EVERY string — a dot on
 * one string is not a repeat sign, exactly as a pipe on one string is not a measure boundary. The
 * dots themselves are looked for on any string, because that is where transcribers put them: on the
 * two inner strings, never on all of them.
 *
 * Adjacent barline columns are one run, so `||o` marks one repeat rather than two.
 *
 * `closingBarColumn` names the synthesised bar past the widest row, if the section has one. Every
 * other bar is column-aligned by construction — an interior bar is only a bar where every string
 * agrees, and the opening one stands at column 0 on every row — so that one is the only bar whose
 * dots have to be looked for per row rather than at a column.
 */
export const repeatMarks = (contents: string[], barlineColumns: number[], closingBarColumn?: number): RepeatMark[] => {
  const marks: RepeatMark[] = []
  const dotAt = (column: number) => contents.some(content => isRepeatDot(content[column]))
  const dotBeforeClosingBar = () => contents.some(content => isRepeatDot(beforeOwnClosingBar(content)))

  for (let i = 0; i < barlineColumns.length; i++) {
    const startColumn = barlineColumns[i]
    let endColumn = startColumn
    while (i + 1 < barlineColumns.length && barlineColumns[i + 1] === endColumn + 1) {
      endColumn = barlineColumns[++i]
    }
    const begins = dotAt(endColumn + 1)
    // The closing bar is always the run's last column, being the largest of them.
    const ends = endColumn === closingBarColumn ? dotBeforeClosingBar() : dotAt(startColumn - 1)
    if (begins || ends) {
      marks.push({ startColumn, endColumn, begins, ends })
    }
  }

  return marks
}

/**
 * The total play count an ornament row states, or undefined for any other row.
 *
 * `x4` and `4x` are the two orders in the wild, and both are read as FOUR PLAYS — see `RepeatSpan`.
 * A count below two states no repetition, so it is not a count; reading `x1` as a repeat would
 * assert structure the row denies.
 */
export const repeatCount = (line: string): number | undefined => {
  const match = /^\(?(?:[xX]\s*(\d+)|(\d+)\s*[xX])\)?$/.exec(line.trim())
  if (!match) {
    return undefined
  }
  const count = Number.parseInt(match[1] ?? match[2], 10)
  return count >= 2 ? count : undefined
}
